import React, { useCallback, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, TextField, TextareaAutosize, Typography, Slider } from "@mui/material";
import { Check, ErrorOutline } from "@mui/icons-material";
import { AppSingleton } from "../services/user_config";

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { VRM, VRMSchema, VRMUtils } from "@pixiv/three-vrm";

import * as Kalidokit from "kalidokit";
import { Face, Pose, Hand } from "kalidokit";
// import "@mediapipe/holistic/holistic";
import { Holistic } from "@mediapipe/holistic/holistic";
// import "@mediapipe/camera_utils/camera_utils";

import {
  drawConnectors,
  drawLandmarks,
  POSE_CONNECTIONS,
  FACEMESH_TESSELATION,
  HAND_CONNECTIONS,
} from "@mediapipe/drawing_utils/drawing_utils";
import { Camera } from "@mediapipe/camera_utils/camera_utils";
// import { poselm3d } from "@mediapipe/pose/pose";
// import "./avatar-mirror-component.css";

const CAMERA_MODE = true;
const DRAW_LANDMARKS = false;
const SHOW_CAMERA_FEED = false;
const ANIMATE_FACE = false;
const ANIMATE_POSE = true;
const ANIMATE_HAND = false;
const LERP_AMOUNT = 0.1; // MARK: default 0.3

const AvatarMirrorComponent = () => {
  // const [detector, setDetector] = useState(null);
  // const [holisticState, setHolisticState] = useState(null);
  const holisticRef = useRef(null);
  const [leftUpperArmRatio, setLeftUpperArmRatio] = useState(1.0);
  const [leftLowerArmRatio, setLeftLowerArmRatio] = useState(1.0); // 1.0
  const [rightUpperArmRatio, setRightUpperArmRatio] = useState(1.0);
  const [rightLowerArmRatio, setRightLowerArmRatio] = useState(1.0); // 2.6
  const leftUpperArmRatioRef = useRef(1.0);
  const leftLowerArmRatioRef = useRef(1.0); // 2.0
  const rightUpperArmRatioRef = useRef(1.0);
  const rightLowerArmRatioRef = useRef(1.0); // 2.6

  useEffect(() => {
    leftUpperArmRatioRef.current = leftUpperArmRatio;
  }, [leftUpperArmRatio]);

  useEffect(() => {
    leftLowerArmRatioRef.current = leftLowerArmRatio;
  }, [leftLowerArmRatio]);

  useEffect(() => {
    rightUpperArmRatioRef.current = rightUpperArmRatio;
  }, [rightUpperArmRatio]);

  useEffect(() => {
    rightLowerArmRatioRef.current = rightLowerArmRatio;
  }, [rightLowerArmRatio]);

  // const init = () => {
  //   const _holistic = new Holistic({
  //     locateFile: (file) => {
  //       return `https://cdn.jsdelivr.net/npm/@mediapipe/holistic@0.4.1633559476/${file}`;
  //     },
  //   });

  //   _holistic.onResults((results) => {
  //     // do something with prediction results
  //     // landmark names may change depending on TFJS/Mediapipe model version
  //     let facelm = results.faceLandmarks;
  //     let poselm = results.poseLandmarks;
  //     let poselm3D = results.ea;
  //     let rightHandlm = results.rightHandLandmarks;
  //     let leftHandlm = results.leftHandLandmarks;

  //     let faceRig = Kalidokit.Face.solve(facelm, { runtime: "mediapipe", video: HTMLVideoElement });
  //     let poseRig = Kalidokit.Pose.solve(poselm3d, poselm, { runtime: "mediapipe", video: HTMLVideoElement });
  //     let rightHandRig = Kalidokit.Hand.solve(rightHandlm, "Right");
  //     let leftHandRig = Kalidokit.Hand.solve(leftHandlm, "Left");
  //   });
  //   setHolistic(_holistic);
  // };

  const startCaptureVideo = () => {
    // const video = document.getElementById("video");
    // video.play();
    const camera = new Camera(HTMLVideoElement, {
      onFrame: async () => {
        await holisticRef.current.send({ image: HTMLVideoElement });
      },
      width: 640,
      height: 480,
    });
    camera.start();
  };

  const processVideo = async () => {
    let videoElement = document.getElementById("input_video") ?? document.querySelector(".input_video");
    // Check if the video is playing
    if (!videoElement.paused && !videoElement.ended) {
      // Send the current frame to the holistic model
      await holisticRef.current.send({ image: videoElement });
    }
    // Call processVideo again for the next frame
    requestAnimationFrame(processVideo);
  };

  const renderModel = () => {
    //Import Helper Functions from Kalidokit
    const remap = Kalidokit.Utils.remap;
    const clamp = Kalidokit.Utils.clamp;
    const lerp = Kalidokit.Vector.lerp;

    /* THREEJS WORLD SETUP */
    let currentVrm;

    // renderer
    const renderer = new THREE.WebGLRenderer({
      // alpha: true,
      // antialias: true,
    });
    // renderer.setSize(window.innerWidth, window.innerHeight);
    // renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth * 0.3, window.innerHeight * 0.3);
    renderer.setPixelRatio(window.devicePixelRatio);
    // MARK:
    let container = document.querySelector("div.container");
    container.appendChild(renderer.domElement);
    // document.body.appendChild(renderer.domElement);

    // camera
    const orbitCamera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 1000);
    // orbitCamera.position.set(0.0, 1.4, 0.7);
    orbitCamera.position.set(0.0, 1.4, 3.0);

    // controls
    const orbitControls = new OrbitControls(orbitCamera, renderer.domElement);
    orbitControls.screenSpacePanning = true;
    orbitControls.target.set(0.0, 1.4, 0.0);
    // orbitControls.target.set(0.0, 0.5, 0.0);
    orbitControls.update();

    // scene
    const scene = new THREE.Scene();

    // light
    const light = new THREE.DirectionalLight(0xffffff);
    light.position.set(1.0, 1.0, 1.0).normalize();
    scene.add(light);

    // Main Render Loop
    const clock = new THREE.Clock();

    function animate() {
      requestAnimationFrame(animate);

      if (currentVrm) {
        // Update model to render physics
        currentVrm.update(clock.getDelta());
      }
      renderer.render(scene, orbitCamera);
    }
    animate();

    /* VRM CHARACTER SETUP */

    // Import Character VRM
    const loader = new GLTFLoader();
    loader.crossOrigin = "anonymous";
    // Import model from URL, add your own model here
    loader.load(
      // "https://cdn.glitch.com/29e07830-2317-4b15-a044-135e73c7f840%2FAshtra.vrm?v=1630342336981",
      // "/models/29e07830-2317-4b15-a044-135e73c7f840_Ashtra.vrm",
      // "/models/little-cat.vrm",
      "/models/el-villa-humano-20241028.vrm",

      (gltf) => {
        VRMUtils.removeUnnecessaryJoints(gltf.scene);

        VRM.from(gltf).then((vrm) => {
          scene.add(vrm.scene);
          currentVrm = vrm;
          currentVrm.scene.rotation.y = Math.PI; // Rotate model 180deg to face camera
        });
      },

      (progress) => console.log("Loading model...", 100.0 * (progress.loaded / progress.total), "%"),

      (error) => console.error(error)
    );

    // Animate Rotation Helper function
    const rigRotation = (name, rotation = { x: 0, y: 0, z: 0 }, dampener = 1, lerpAmount = 0.3) => {
      if (!currentVrm) {
        return;
      }
      const Part = currentVrm.humanoid.getBoneNode(VRMSchema.HumanoidBoneName[name]);
      if (!Part) {
        return;
      }

      let euler = new THREE.Euler(rotation.x * dampener, rotation.y * dampener, rotation.z * dampener);
      let quaternion = new THREE.Quaternion().setFromEuler(euler);
      Part.quaternion.slerp(quaternion, lerpAmount); // interpolate
    };

    // Animate Position Helper Function
    const rigPosition = (name, position = { x: 0, y: 0, z: 0 }, dampener = 1, lerpAmount = 0.3) => {
      if (!currentVrm) {
        return;
      }
      const Part = currentVrm.humanoid.getBoneNode(VRMSchema.HumanoidBoneName[name]);
      if (!Part) {
        return;
      }
      let vector = new THREE.Vector3(position.x * dampener, position.y * dampener, position.z * dampener);
      Part.position.lerp(vector, lerpAmount); // interpolate
    };

    let oldLookTarget = new THREE.Euler();
    const rigFace = (riggedFace) => {
      if (!currentVrm) {
        return;
      }
      rigRotation("Neck", riggedFace.head, 0.7);

      // Blendshapes and Preset Name Schema
      const Blendshape = currentVrm.blendShapeProxy;
      const PresetName = VRMSchema.BlendShapePresetName;

      // Simple example without winking. Interpolate based on old blendshape, then stabilize blink with `Kalidokit` helper function.
      // for VRM, 1 is closed, 0 is open.
      // riggedFace.eye.l = lerp(clamp(1 - riggedFace.eye.l, 0, 1), Blendshape.getValue(PresetName.Blink), 0.5);
      // riggedFace.eye.r = lerp(clamp(1 - riggedFace.eye.r, 0, 1), Blendshape.getValue(PresetName.Blink), 0.5);
      riggedFace.eye = Kalidokit.Face.stabilizeBlink(riggedFace.eye, riggedFace.head.y);
      // Blendshape.setValue(PresetName.Blink, riggedFace.eye.l);

      // Interpolate and set mouth blendshapes
      // Blendshape.setValue(PresetName.I, lerp(riggedFace.mouth.shape.I, Blendshape.getValue(PresetName.I), 0.5));
      // Blendshape.setValue(PresetName.A, lerp(riggedFace.mouth.shape.A, Blendshape.getValue(PresetName.A), 0.5));
      // Blendshape.setValue(PresetName.E, lerp(riggedFace.mouth.shape.E, Blendshape.getValue(PresetName.E), 0.5));
      // Blendshape.setValue(PresetName.O, lerp(riggedFace.mouth.shape.O, Blendshape.getValue(PresetName.O), 0.5));
      // Blendshape.setValue(PresetName.U, lerp(riggedFace.mouth.shape.U, Blendshape.getValue(PresetName.U), 0.5));

      //PUPILS
      //interpolate pupil and keep a copy of the value
      let lookTarget = new THREE.Euler(
        lerp(oldLookTarget.x, riggedFace.pupil.y, 0.4),
        lerp(oldLookTarget.y, riggedFace.pupil.x, 0.4),
        0,
        "XYZ"
      );
      oldLookTarget.copy(lookTarget);
      currentVrm.lookAt.applyer.lookAt(lookTarget);
    };

    /* VRM Character Animator */
    const animateVRM = (vrm, results) => {
      if (!vrm) {
        return;
      }
      // Take the results from `Holistic` and animate character based on its Face, Pose, and Hand Keypoints.
      let riggedPose, riggedLeftHand, riggedRightHand, riggedFace;

      const faceLandmarks = results.faceLandmarks;
      // Pose 3D Landmarks are with respect to Hip distance in meters
      const pose3DLandmarks = results.ea;
      // Pose 2D landmarks are with respect to videoWidth and videoHeight
      const pose2DLandmarks = results.poseLandmarks;
      // Be careful, hand landmarks may be reversed
      const leftHandLandmarks = results.rightHandLandmarks;
      const rightHandLandmarks = results.leftHandLandmarks;

      // Animate Face
      if (ANIMATE_FACE && faceLandmarks) {
        riggedFace = Kalidokit.Face.solve(faceLandmarks, {
          runtime: "mediapipe",
          video: videoElement,
        });
        rigFace(riggedFace);
      }

      // Animate Pose
      if (ANIMATE_POSE && pose2DLandmarks && pose3DLandmarks) {
        riggedPose = Kalidokit.Pose.solve(pose3DLandmarks, pose2DLandmarks, {
          runtime: "mediapipe",
          video: videoElement,
        });

        rigRotation("Hips", riggedPose.Hips.rotation, 0.7);
        rigPosition(
          "Hips",
          {
            x: -riggedPose.Hips.position.x, // Reverse direction
            y: riggedPose.Hips.position.y + 1, // Add a bit of height // MARK originally  y + 1
            z: -riggedPose.Hips.position.z, // Reverse direction
          },
          1,
          0.07
        );

        rigRotation("Chest", riggedPose.Spine, 0.25, 0.3);
        rigRotation("Spine", riggedPose.Spine, 0.45, 0.3);

        rigRotation("LeftUpperArm", riggedPose.LeftUpperArm, leftUpperArmRatioRef.current, 0.3);
        rigRotation("LeftLowerArm", riggedPose.LeftLowerArm, leftLowerArmRatioRef.current, 0.3);
        // rigRotation("LeftShoulder", riggedPose.LeftShoulder, 1, 0.3);
        rigRotation("LeftHand", riggedPose.LeftHand, 1, 0.3);
        rigRotation("RightUpperArm", riggedPose.RightUpperArm, rightUpperArmRatioRef.current, 0.3);
        // rigRotation(
        //   "RightUpperArm",
        //   { ...riggedPose.RightUpperArm, x: riggedPose.RightUpperArm.x * 0.8, y: riggedPose.RightUpperArm.y * 0.8 },
        //   rightUpperArmRatioRef.current,
        //   0.3
        // );
        rigRotation("RightLowerArm", riggedPose.RightLowerArm, rightLowerArmRatioRef.current, 0.3);
        // rigRotation(
        //   "RightLowerArm",
        //   { ...riggedPose.RightLowerArm, x:riggedPose.RightLowerArm.x * 0.8, y: riggedPose.RightLowerArm.y * 2.5 },
        //   rightLowerArmRatioRef.current,
        //   0.3
        // );
        rigRotation("RightHand", riggedPose.RightHand, 1, 0.3);

        rigRotation("LeftUpperLeg", riggedPose.LeftUpperLeg, 1, 0.3);
        rigRotation("LeftLowerLeg", riggedPose.LeftLowerLeg, 1, 0.3);
        rigRotation("RightUpperLeg", riggedPose.RightUpperLeg, 1, 0.3);
        rigRotation("RightLowerLeg", riggedPose.RightLowerLeg, 1, 0.3);
      }

      // Animate Hands
      if (ANIMATE_HAND & leftHandLandmarks) {
        riggedLeftHand = Kalidokit.Hand.solve(leftHandLandmarks, "Left");
        rigRotation("LeftHand", {
          // Combine pose rotation Z and hand rotation X Y
          z: riggedPose.LeftHand.z,
          y: riggedLeftHand.LeftWrist.y,
          x: riggedLeftHand.LeftWrist.x,
        });
        rigRotation("LeftRingProximal", riggedLeftHand.LeftRingProximal);
        rigRotation("LeftRingIntermediate", riggedLeftHand.LeftRingIntermediate);
        rigRotation("LeftRingDistal", riggedLeftHand.LeftRingDistal);
        rigRotation("LeftIndexProximal", riggedLeftHand.LeftIndexProximal);
        rigRotation("LeftIndexIntermediate", riggedLeftHand.LeftIndexIntermediate);
        rigRotation("LeftIndexDistal", riggedLeftHand.LeftIndexDistal);
        rigRotation("LeftMiddleProximal", riggedLeftHand.LeftMiddleProximal);
        rigRotation("LeftMiddleIntermediate", riggedLeftHand.LeftMiddleIntermediate);
        rigRotation("LeftMiddleDistal", riggedLeftHand.LeftMiddleDistal);
        rigRotation("LeftThumbProximal", riggedLeftHand.LeftThumbProximal);
        rigRotation("LeftThumbIntermediate", riggedLeftHand.LeftThumbIntermediate);
        rigRotation("LeftThumbDistal", riggedLeftHand.LeftThumbDistal);
        rigRotation("LeftLittleProximal", riggedLeftHand.LeftLittleProximal);
        rigRotation("LeftLittleIntermediate", riggedLeftHand.LeftLittleIntermediate);
        rigRotation("LeftLittleDistal", riggedLeftHand.LeftLittleDistal);
      }
      if (ANIMATE_HAND & rightHandLandmarks) {
        riggedRightHand = Kalidokit.Hand.solve(rightHandLandmarks, "Right");
        rigRotation("RightHand", {
          // Combine Z axis from pose hand and X/Y axis from hand wrist rotation
          z: riggedPose.RightHand.z,
          y: riggedRightHand.RightWrist.y,
          x: riggedRightHand.RightWrist.x,
        });
        rigRotation("RightRingProximal", riggedRightHand.RightRingProximal);
        rigRotation("RightRingIntermediate", riggedRightHand.RightRingIntermediate);
        rigRotation("RightRingDistal", riggedRightHand.RightRingDistal);
        rigRotation("RightIndexProximal", riggedRightHand.RightIndexProximal);
        rigRotation("RightIndexIntermediate", riggedRightHand.RightIndexIntermediate);
        rigRotation("RightIndexDistal", riggedRightHand.RightIndexDistal);
        rigRotation("RightMiddleProximal", riggedRightHand.RightMiddleProximal);
        rigRotation("RightMiddleIntermediate", riggedRightHand.RightMiddleIntermediate);
        rigRotation("RightMiddleDistal", riggedRightHand.RightMiddleDistal);
        rigRotation("RightThumbProximal", riggedRightHand.RightThumbProximal);
        rigRotation("RightThumbIntermediate", riggedRightHand.RightThumbIntermediate);
        rigRotation("RightThumbDistal", riggedRightHand.RightThumbDistal);
        rigRotation("RightLittleProximal", riggedRightHand.RightLittleProximal);
        rigRotation("RightLittleIntermediate", riggedRightHand.RightLittleIntermediate);
        rigRotation("RightLittleDistal", riggedRightHand.RightLittleDistal);
      }
    };

    /* SETUP MEDIAPIPE HOLISTIC INSTANCE */
    let videoElement = document.getElementById("input_video") ?? document.querySelector(".input_video");
    let guideCanvas = document.querySelector("canvas.guides");
    // console.log("guideCanvas");
    // console.log(guideCanvas);

    const onResults = (results) => {
      // Draw landmark guides
      if (DRAW_LANDMARKS) {
        drawResults(results);
      }
      // Animate model
      animateVRM(currentVrm, results);
    };

    const holistic = new Holistic({
      locateFile: (file) => {
        console.log(`file: ${file}`);
        // holistic_solution_packed_assets_loader.js
        // holistic_solution_simd_wasm_bin.js
        // holistic.binarypb
        // holistic_solution_packed_assets.data
        // holistic_solution_simd_wasm_bin.wasm
        // pose_landmark_full.tflite
        return `https://cdn.jsdelivr.net/npm/@mediapipe/holistic@0.4.1633559476/${file}`;
        // return `https://cdn.jsdelivr.net/npm/@mediapipe/holistic@0.5.1635989137/${file}`;
      },
    });

    holistic.setOptions({
      modelComplexity: 0, // MARK: 1: Simple model; 2: Accurate modedl | 姿势地标模型的复杂度：0、1 或 2。地标准确度和推理延迟通常随着模型复杂度的增加而增加
      smoothLandmarks: true, // 如果设置为true，解决方案过滤不同的输入图像上的姿势地标以减少抖动
      // static_image_mode: false,  // 如果设置为 false，该解决方案会将输入图像视为视频流。它将尝试在第一张图像中检测最突出的人，并在成功检测后进一步定位姿势地标。在随后的图像中，它只是简单地跟踪那些地标，而不会调用另一个检测，直到它失去跟踪，以减少计算和延迟。如果设置为 true，则人员检测会运行每个输入图像，非常适合处理一批静态的、可能不相关的图像。默认为false。
      // enableSegmentation: false,
      // smoothSegmentation: false,
      upper_body_only: true,
      minDetectionConfidence: 0.7,
      minTrackingConfidence: 0.7,
      refineFaceLandmarks: false,
    });
    // Pass holistic a callback function
    holistic.onResults(onResults);

    const drawResults = (results) => {
      // guideCanvas.width = videoElement.videoWidth;
      // guideCanvas.height = videoElement.videoHeight;
      guideCanvas.width = 640;
      guideCanvas.height = 480;
      let canvasCtx = guideCanvas.getContext("2d");
      canvasCtx.save();
      canvasCtx.clearRect(0, 0, guideCanvas.width, guideCanvas.height);
      // Use `Mediapipe` drawing functions
      drawConnectors(canvasCtx, results.poseLandmarks, POSE_CONNECTIONS, {
        color: "#00cff7",
        lineWidth: 4,
      });
      drawLandmarks(canvasCtx, results.poseLandmarks, {
        color: "#ff0364",
        lineWidth: 2,
      });
      drawConnectors(canvasCtx, results.faceLandmarks, FACEMESH_TESSELATION, {
        color: "#C0C0C070",
        lineWidth: 1,
      });
      if (results.faceLandmarks && results.faceLandmarks.length === 478) {
        //draw pupils
        drawLandmarks(canvasCtx, [results.faceLandmarks[468], results.faceLandmarks[468 + 5]], {
          color: "#ffe603",
          lineWidth: 2,
        });
      }
      drawConnectors(canvasCtx, results.leftHandLandmarks, HAND_CONNECTIONS, {
        color: "#eb1064",
        lineWidth: 5,
      });
      drawLandmarks(canvasCtx, results.leftHandLandmarks, {
        color: "#00cff7",
        lineWidth: 2,
      });
      drawConnectors(canvasCtx, results.rightHandLandmarks, HAND_CONNECTIONS, {
        color: "#22c3e3",
        lineWidth: 5,
      });
      drawLandmarks(canvasCtx, results.rightHandLandmarks, {
        color: "#ff0364",
        lineWidth: 2,
      });
    };

    if (CAMERA_MODE) {
      // Use `Mediapipe` utils to get camera - lower resolution = higher fps
      const camera = new Camera(videoElement, {
        onFrame: async () => {
          await holistic.send({ image: videoElement });
        },
        width: 640,
        height: 480,
      });
      camera.start();
    } else {
      processVideo();
    }

    holisticRef.current = holistic;
  };

  useEffect(() => {
    // init();
    renderModel();
  }, []);

  return (
    <>
      <div
        // className="previewx"
        style={{
          display: "flex",
          flexDirection: "column",
          position: "absolute",
          top: 0,
          right: 0,
          borderRadius: "8px",
          background: "#222",
        }}
      >
        <div className="container" />
        <video
          id="input_video"
          className="input_video"
          style={{
            position: "absolute",
            bottom: -160,
            right: 0,
            height: "auto",
            width: "100%",
          }}
          src="/video/V1.1.mp4"
          width="640"
          height="480"
          autoPlay
          muted
          hidden={!SHOW_CAMERA_FEED}
          playsInline
        ></video>
        <canvas className="guides"></canvas>
      </div>
      {/* <Button sx={{ position: "absolute", bottom: 0, left: 0, zIndex: 999 }} onClick={() => startCaptureVideo()}>
        Start Capture
      </Button> */}
    </>
  );
};

const f = () => {};

export { AvatarMirrorComponent, f };
